import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Play, Search, Bell, Sparkles } from "lucide-react";
import NotificationPreferences from "@/components/NotificationPreferences";
import SearchOverlay from "@/components/SearchOverlay";

export default function MobileBottomNav() {
  const loc = useLocation();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(true); // assume subscribed until we know

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, [prefsOpen]);

  const isActive = (path, exact = false) =>
    exact ? loc.pathname === path : loc.pathname.startsWith(path);

  const itemCls = (active) =>
    "flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 transition";
  const styleFor = (active) => ({ color: active ? "var(--accent)" : "var(--text-muted)" });
  const labelCls = "text-[10px] font-bold uppercase tracking-wider";

  // Bell needs animation only when NOT subscribed
  const bellAnimClass = subscribed ? "" : "mobile-bell-blink";

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t flex items-stretch justify-around"
        style={{
          background: "var(--bg)",
          borderColor: "var(--border)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        data-testid="mobile-bottom-nav"
      >
        {/* INICIO */}
        <Link to="/" className={itemCls()} style={styleFor(isActive("/", true))} data-testid="mobile-nav-home">
          <Home size={20} strokeWidth={isActive("/", true) ? 2.5 : 2} fill={isActive("/", true) ? "currentColor" : "none"} />
          <span className={labelCls}>Inicio</span>
        </Link>

        {/* SHORTS → /shorts index */}
        <Link to="/shorts" className={itemCls()} style={styleFor(isActive("/shorts"))} data-testid="mobile-nav-shorts">
          <Play size={20} strokeWidth={isActive("/shorts") ? 2.5 : 2} fill={isActive("/shorts") ? "currentColor" : "none"} />
          <span className={labelCls}>Shorts</span>
        </Link>

        {/* POPU IA (center, prominent) */}
        <Link to="/popu-ia" className="flex-1 flex items-center justify-center" data-testid="mobile-nav-popu-ia">
          <span
            className="flex items-center justify-center w-12 h-12 -mt-5 rounded-full shadow-lg transition-transform active:scale-95"
            style={{
              background: "var(--accent)",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(255, 27, 107, 0.45)",
            }}
            aria-label="POPU IA"
          >
            <Sparkles size={22} strokeWidth={2.4} />
          </span>
        </Link>

        {/* BUSCAR → fullscreen overlay */}
        <button onClick={() => setSearchOpen(true)} className={itemCls()} style={styleFor(false)} data-testid="mobile-nav-search">
          <Search size={20} strokeWidth={2} />
          <span className={labelCls}>Buscar</span>
        </button>

        {/* ALERTAS → notif prefs modal, bell blinks if NOT subscribed */}
        <button onClick={() => setPrefsOpen(true)} className={itemCls()} style={styleFor(prefsOpen)} data-testid="mobile-nav-alerts">
          <span className={`relative ${bellAnimClass}`}>
            <Bell size={20} strokeWidth={subscribed ? 2.5 : 2.2} fill={subscribed ? "currentColor" : "none"} />
            {!subscribed && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500">
                <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-75" />
              </span>
            )}
          </span>
          <span className={labelCls}>Alertas</span>
        </button>
      </nav>
      <div className="md:hidden h-16" aria-hidden="true" />
      {prefsOpen && <NotificationPreferences onClose={() => setPrefsOpen(false)} />}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
